import { useState, useEffect } from 'react';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useSupplierSearch } from '@/hooks/useSupplierSearch';

interface SupplierTypeaheadProps {
  value: string;
  onSelect: (supplierId: string, supplierName: string) => void;
  disabled?: boolean;
}

export function SupplierTypeahead({ value, onSelect, disabled }: SupplierTypeaheadProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { data, isLoading, error } = useSupplierSearch(''); // Load all suppliers initially

  const allSuppliers = (data as any)?.suppliers || [];
  const selectedSupplier = allSuppliers.find((s: any) => s.id === value);
  
  // Client-side filtering for better performance
  const filteredSuppliers = allSuppliers.filter((supplier: any) => {
    if (searchQuery.length < 2) return false;
    const query = searchQuery.toLowerCase();
    return (
      supplier.name?.toLowerCase().includes(query) ||
      supplier.supplierName?.toLowerCase().includes(query) ||
      supplier.shortName?.toLowerCase().includes(query)
    );
  });
  
  console.log('[SupplierTypeahead] Search query:', searchQuery);
  console.log('[SupplierTypeahead] All suppliers:', allSuppliers.length);
  console.log('[SupplierTypeahead] Filtered suppliers:', filteredSuppliers.length);
  console.log('[SupplierTypeahead] Loading:', isLoading);
  console.log('[SupplierTypeahead] Error:', error);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          disabled={disabled}
        >
          {selectedSupplier ? (
            <div className="flex items-center gap-2">
              {selectedSupplier.logo && (
                <img 
                  src={selectedSupplier.logo} 
                  alt={selectedSupplier.supplierName || selectedSupplier.name} 
                  className="h-5 w-5 object-contain"
                />
              )}
              <span>{selectedSupplier.supplierName || selectedSupplier.name}</span>
            </div>
          ) : (
            'Select supplier...'
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search suppliers (min 2 chars)..."
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            {error && (
              <div className="p-4 text-sm text-destructive border-l-2 border-destructive/50 bg-destructive/10 m-2 rounded">
                {error.message.includes('Authentication') || error.message.includes('log in')
                  ? '🔒 Please log in to search suppliers' 
                  : '❌ Failed to load suppliers. Please try again.'}
              </div>
            )}
            {isLoading && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">Loading suppliers...</span>
              </div>
            )}
            {!isLoading && !error && searchQuery.length < 2 && (
              <CommandEmpty>
                <div className="text-center py-4">
                  <div className="text-sm text-muted-foreground">Type at least 2 characters to search</div>
                  <div className="text-xs text-muted-foreground mt-1">Available suppliers: {allSuppliers.length}</div>
                </div>
              </CommandEmpty>
            )}
            {!isLoading && !error && searchQuery.length >= 2 && filteredSuppliers.length === 0 && (
              <CommandEmpty>
                <div className="text-center py-4">
                  <div className="text-sm text-muted-foreground">No suppliers found for "{searchQuery}"</div>
                  <div className="text-xs text-muted-foreground mt-1">Try a different search term</div>
                </div>
              </CommandEmpty>
            )}
            {!isLoading && !error && filteredSuppliers.length > 0 && (
              <CommandGroup>
                {filteredSuppliers.map((supplier: any) => (
                  <CommandItem
                    key={supplier.id}
                    value={supplier.id}
                    onSelect={() => {
                      onSelect(supplier.id, supplier.supplierName || supplier.name);
                      setOpen(false);
                    }}
                    className="flex items-center gap-2 cursor-pointer hover:bg-accent"
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4 flex-shrink-0',
                        value === supplier.id ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    {supplier.logo && (
                      <img 
                        src={supplier.logo} 
                        alt={supplier.supplierName || supplier.name} 
                        className="h-5 w-5 object-contain flex-shrink-0"
                      />
                    )}
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="font-medium truncate">{supplier.supplierName || supplier.name}</span>
                      {supplier.shortName && supplier.shortName !== supplier.supplierName && (
                        <span className="text-xs text-muted-foreground truncate">{supplier.shortName}</span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
